/**
 * Stats.jsx — dedicated stats page for the user's scanning history.
 *
 * Layout (mobile-first, scales to a 2-3-col grid on desktop):
 *   1. Headline counts (total / active / owned / unidentified) as flip cards
 *   2. Genre breakdown — donut chart with legend
 *   3. Top artists — horizontal bar list
 *   4. Scan velocity — bar chart, last 90 days
 *   5. Mood profile — avg BPM, top key, BPM histogram (flip card)
 *   6. Source mix — donut chart (Spotify/Shazam/Apple/etc.)
 *
 * Each "headline" card uses the `.flip-card` CSS class (defined in
 * index.css) to flip on hover (desktop) or tap (mobile) and reveal a
 * secondary stat on the back.
 *
 * All charts use `recharts` (already installed). Theme colors are pulled
 * from the design tokens via CSS variables — no hardcoded hex.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Disc3,
  BadgeCheck,
  AlertTriangle,
  Activity,
  Music,
  ArrowLeft,
  TrendingUp,
  Sparkles,
  RefreshCcw,
} from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const PALETTE = [
  'hsl(174 78% 50%)',  // teal
  '#DBFF3A',            // lime
  '#FFC85C',            // amber
  '#FF7A8A',            // coral
  '#9C7BFF',            // purple
  '#3FC9FF',            // sky
  '#7CE0C0',            // mint
  '#FFB36B',            // peach
];

const SOURCE_LABEL = {
  spotify: 'Spotify',
  shazam: 'Shazam',
  apple_music: 'Apple Music',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube_music: 'YouTube Music',
  soundcloud: 'SoundCloud',
  bandcamp: 'Bandcamp',
  beatport: 'Beatport',
  album_art: 'Album art',
  poster: 'Poster',
  ai_discovery: 'AI Find',
  other: 'Other',
  unknown: 'Unknown',
};

export default function Stats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: payload } = await api.get('/stats/detailed');
        if (active) setData(payload);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load stats');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  /** Fill missing days with zero counts for a smooth velocity chart. */
  const velocityPoints = useMemo(() => {
    if (!data?.scan_velocity) return [];
    const byDate = new Map(data.scan_velocity.map((p) => [p.date, p.count]));
    const today = new Date();
    const out = [];
    for (let i = 89; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      out.push({
        date: iso,
        // Short label for the X axis (e.g., "Jun 12")
        label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        count: byDate.get(iso) || 0,
      });
    }
    return out;
  }, [data]);

  // Empty / loading states ------------------------------------------------
  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <Header />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={`s-h-${i}`} className="h-28 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-72 rounded-2xl mb-4" />
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }

  if (!data || data.totals?.total === 0) {
    return (
      <div className="max-w-5xl mx-auto">
        <Header />
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center mt-6">
          <Sparkles className="w-10 h-10 mx-auto text-[hsl(var(--primary))] mb-3" />
          <h2 className="font-display text-xl font-semibold">No stats yet</h2>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
            Scan a few snapshots and your collection stats will start filling in here —
            top genres, artists, scan streaks, BPM profile and more.
          </p>
          <Link to="/upload">
            <Button className="mt-5 rounded-xl" data-testid="stats-empty-cta">
              Start scanning
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const totals = data.totals;

  return (
    <div className="max-w-5xl mx-auto pb-16">
      <Header />

      {/* ----- Headline flip cards ----- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <FlipStatCard
          icon={Disc3}
          accent="primary"
          label="Total pulls"
          value={totals.total}
          backLabel="Active"
          backValue={totals.active}
          backHint="Tracks currently in your crates"
          testid="stat-flip-total"
        />
        <FlipStatCard
          icon={BadgeCheck}
          accent="lime"
          label="Owned"
          value={totals.owned}
          backLabel="% of collection"
          backValue={totals.total > 0 ? `${Math.round((totals.owned / totals.total) * 100)}%` : '—'}
          backHint="Tracks you have on file"
          testid="stat-flip-owned"
        />
        <FlipStatCard
          icon={AlertTriangle}
          accent="coral"
          label="To fix"
          value={totals.unidentified}
          backLabel="Unidentified"
          backValue={totals.unidentified}
          backHint="Snapshots that need a manual fix"
          testid="stat-flip-unid"
        />
        <FlipStatCard
          icon={Music}
          accent="amber"
          label="Genres"
          value={(data.genre_breakdown || []).length}
          backLabel="Top genre"
          backValue={data.genre_breakdown?.[0]?.genre || '—'}
          backHint={`${data.genre_breakdown?.[0]?.count || 0} tracks`}
          testid="stat-flip-genres"
        />
      </div>

      {/* ----- Genre breakdown ----- */}
      <Section title="Genre breakdown" icon={Music} testid="stats-genre-section">
        <div className="grid md:grid-cols-2 gap-4 items-center">
          {data.genre_breakdown?.length > 0 ? (
            <>
              <div className="h-64 sm:h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.genre_breakdown.slice(0, 10)}
                      dataKey="count"
                      nameKey="genre"
                      innerRadius="55%"
                      outerRadius="85%"
                      paddingAngle={2}
                      stroke="hsl(var(--background))"
                      strokeWidth={2}
                    >
                      {data.genre_breakdown.slice(0, 10).map((_, i) => (
                        <Cell key={`g-cell-${i}`} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-1.5 text-sm">
                {data.genre_breakdown.slice(0, 10).map((g, i) => (
                  <li
                    key={g.genre}
                    className="flex items-center gap-2"
                    data-testid={`stats-genre-row-${g.genre}`}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-sm shrink-0"
                      style={{ background: PALETTE[i % PALETTE.length] }}
                      aria-hidden="true"
                    />
                    <span className="flex-1 truncate font-medium">{g.genre}</span>
                    <span className="font-mono text-xs text-muted-foreground">{g.count}</span>
                  </li>
                ))}
                {data.genre_breakdown.length > 10 && (
                  <li className="text-xs text-muted-foreground font-mono pt-1">
                    + {data.genre_breakdown.length - 10} more
                  </li>
                )}
              </ul>
            </>
          ) : (
            <EmptyHint label="No genre data yet" />
          )}
        </div>
      </Section>

      {/* ----- Top artists ----- */}
      <Section title="Top artists" icon={TrendingUp} testid="stats-artists-section">
        {data.top_artists?.length > 0 ? (
          <ul className="space-y-2" data-testid="stats-artists-list">
            {data.top_artists.map((a, i) => {
              const max = data.top_artists[0].count || 1;
              const pct = Math.max(6, Math.round((a.count / max) * 100));
              return (
                <li key={a.artist} className="flex items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground w-5 text-right shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-medium truncate text-sm">{a.artist}</span>
                      <span className="font-mono text-xs text-muted-foreground shrink-0">
                        {a.count}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[hsl(var(--primary))]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyHint label="No artist data yet" />
        )}
      </Section>

      {/* ----- Scan velocity ----- */}
      <Section title="Scan velocity (last 90 days)" icon={Activity} testid="stats-velocity-section">
        <div className="h-56 sm:h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={velocityPoints} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <XAxis
                dataKey="label"
                interval={Math.floor(velocityPoints.length / 6)}
                stroke="hsl(var(--muted-foreground))"
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                width={28}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'hsl(var(--secondary))', opacity: 0.4 }} />
              <Bar dataKey="count" radius={[3, 3, 0, 0]} fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* ----- Mood profile (flip card on its own) ----- */}
      <Section title="Mood profile" icon={Activity} testid="stats-mood-section">
        {data.bpm_profile?.sample_size > 0 ? (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="grid grid-cols-2 gap-3">
              <FlipStatCard
                icon={Activity}
                accent="primary"
                label="Avg BPM"
                value={data.bpm_profile.avg_bpm || '—'}
                backLabel="Median BPM"
                backValue={data.bpm_profile.median_bpm || '—'}
                backHint={`Across ${data.bpm_profile.sample_size} tracks with BPM data`}
                size="md"
                testid="stat-flip-bpm"
              />
              <FlipStatCard
                icon={Music}
                accent="lime"
                label="Top key"
                value={data.bpm_profile.top_key || '—'}
                backLabel="Sample size"
                backValue={data.bpm_profile.sample_size}
                backHint="Tracks with key data"
                size="md"
                testid="stat-flip-key"
              />
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.bpm_profile.distribution} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <XAxis
                    dataKey="range"
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fontSize: 9 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    width={28}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'hsl(var(--secondary))', opacity: 0.4 }} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]} fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <EmptyHint label="No BPM data yet — run “Look up BPM &amp; Key” from your Profile." />
        )}
      </Section>

      {/* ----- Source mix ----- */}
      <Section title="Where you scan from" icon={BarChart3} testid="stats-source-section">
        {data.source_mix?.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-4 items-center">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.source_mix}
                    dataKey="count"
                    nameKey="source"
                    innerRadius="55%"
                    outerRadius="85%"
                    paddingAngle={2}
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                  >
                    {data.source_mix.map((_, i) => (
                      <Cell key={`s-cell-${i}`} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip labelMap={SOURCE_LABEL} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="space-y-1.5 text-sm">
              {data.source_mix.map((s, i) => (
                <li key={s.source} className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ background: PALETTE[i % PALETTE.length] }}
                    aria-hidden="true"
                  />
                  <span className="flex-1 truncate font-medium">
                    {SOURCE_LABEL[s.source] || s.source}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">{s.count}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <EmptyHint label="No source data yet" />
        )}
      </Section>
    </div>
  );
}

/* ============================================================
 * Presentational helpers
 * ============================================================ */

function Header() {
  return (
    <div className="flex items-center justify-between gap-3 mb-5">
      <div className="min-w-0">
        <Link
          to="/crate"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
          data-testid="stats-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Collection
        </Link>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">
          Your stats
        </h1>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 sm:p-5 mb-4"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[hsl(var(--primary))]" />
        <h2 className="font-display font-semibold">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function EmptyHint({ label }) {
  return (
    <div className="text-sm text-muted-foreground italic py-4 text-center">{label}</div>
  );
}

const ACCENT_TEXT = {
  primary: 'text-[hsl(var(--primary))]',
  lime: 'text-[var(--brand-lime)]',
  amber: 'text-[var(--brand-amber)]',
  coral: 'text-[var(--brand-coral)]',
};
const ACCENT_BG = {
  primary: 'bg-[hsl(var(--primary))]/10',
  lime: 'bg-[var(--brand-lime)]/10',
  amber: 'bg-[var(--brand-amber)]/10',
  coral: 'bg-[var(--brand-coral)]/10',
};

/**
 * FlipStatCard — a stat tile that flips on hover (desktop) or tap
 * (mobile/touch). Front shows the headline number; back shows a
 * secondary related stat. Tap toggles `.is-flipped` (CSS rule lives in
 * index.css so a single source of truth governs the 3D rotation).
 */
function FlipStatCard({ icon: Icon, accent = 'primary', label, value, backLabel, backValue, backHint, size = 'lg', testid }) {
  const [flipped, setFlipped] = useState(false);
  const valueClass = size === 'md' ? 'text-2xl' : 'text-3xl sm:text-4xl';
  const heightClass = size === 'md' ? 'h-24' : 'h-28';
  return (
    <button
      type="button"
      onClick={() => setFlipped((f) => !f)}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          setFlipped((f) => !f);
        }
      }}
      data-testid={testid}
      aria-label={`${label}: ${value}. Tap to see related stat.`}
      className={`flip-card ${flipped ? 'is-flipped' : ''} ${heightClass} relative w-full text-left rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]`}
    >
      <div className="flip-card-inner">
        {/* Front */}
        <div className={`flip-card-face rounded-2xl border border-border bg-card p-3 sm:p-4 ${ACCENT_BG[accent] ? '' : ''}`}>
          {/* Persistent discoverability hint — always visible (small + muted)
              so users know the card flips without having to hover-discover. */}
          <div
            className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] font-mono text-muted-foreground pointer-events-none"
            aria-hidden="true"
          >
            <RefreshCcw className="w-2.5 h-2.5" /> flip
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className={`w-7 h-7 rounded-lg grid place-items-center ${ACCENT_BG[accent]} ${ACCENT_TEXT[accent]}`}>
              <Icon className="w-4 h-4" />
            </span>
            <span className="truncate">{label}</span>
          </div>
          <div className={`font-display font-semibold mt-1 ${valueClass}`}>{value}</div>
        </div>
        {/* Back */}
        <div className="flip-card-face flip-card-back rounded-2xl border border-[hsl(var(--primary))]/30 bg-card p-3 sm:p-4">
          <div className="text-[10px] uppercase font-mono text-[hsl(var(--primary))]">
            {backLabel}
          </div>
          <div className={`font-display font-semibold mt-1 ${valueClass}`}>{backValue}</div>
          {backHint && (
            <div className="text-[10px] text-muted-foreground mt-1 leading-tight">
              {backHint}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

/** Custom recharts tooltip styled to match the dark theme. */
function ChartTooltip({ active, payload, labelMap }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const rawName = p.name || p.payload?.label || '';
  const name = labelMap ? labelMap[rawName] || rawName : rawName;
  return (
    <div className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs shadow-lg">
      <div className="font-medium text-foreground">{name}</div>
      <div className="font-mono text-[hsl(var(--primary))]">
        {p.value} {p.value === 1 ? 'track' : 'tracks'}
      </div>
    </div>
  );
}
